// Betrachtungszeitraum preset resolver — shared by Aggregation KPI and
// Efficiency KPI (and any future tile that adopts the same pattern).
//
// Pure functions only (no React/API dependency). Reuses getPresetRange from
// utils/berlinDate.js for calendar/rolling boundary math (Berlin/DST-safe,
// already used across the app) — this module only adds the "which preset is
// selected" bookkeeping and the previous-period table from the Aggregation
// KPI user story (also adopted verbatim by Efficiency KPI).

import { getPresetRange } from '../../../utils/berlinDate';
import { DATE_PRESET_GROUPS, ALL_DATE_PRESET_KEYS, presetLabelKey } from '../../../utils/datePresets';

export const ALL_TIME_PRESET = { key: 'all', label: 'Gesamter Zeitraum' };
export const CUSTOM_PRESET = { key: 'custom', label: 'Custom' };

/**
 * Same shape the old PERIOD_PRESET_GROUPS const had, plus a stable `id`, but
 * built from the single shared key table in utils/datePresets.js instead of a
 * second hand-maintained copy of the same 14 labels. (The previous comment here
 * asked maintainers to "keep it in sync" with DateRangePresets.jsx — it is now
 * literally the same table, so there is nothing left to keep in sync.)
 *
 * A function rather than a const because the labels are translated and module
 * scope has no access to `t`. The KEY table stays a plain const on purpose —
 * detectPeriodPresetFromDateRange below is a pure, non-React function and must
 * be able to read the keys without a `t`.
 *
 * Use `group.id` as the React key, never `group.label`: a translated string as
 * a key remounts the whole group on every language switch.
 */
export const getPeriodPresetGroups = (t) =>
  DATE_PRESET_GROUPS.map((group) => ({
    id: group.id,
    label: t(group.labelKey),
    presets: group.keys.map((key) => ({ key, label: t(presetLabelKey(key)) })),
  }));

// Named lookup for the subset of calendar presets the user story's
// Vorperioden-Tabelle defines explicitly. Every other preset (yesterday,
// lastWeek, last2Weeks selected directly, and all rolling presets + custom)
// falls through to the generic "equal-length window immediately before"
// branch in resolvePreviousRange, which is exactly correct for them too
// since their own current range is already a fixed or duration-based window.
const PREVIOUS_CALENDAR_PRESET = {
  today: 'yesterday',
  thisWeek: 'lastWeek',
  thisMonth: 'lastMonth',
  thisYear: 'lastYear',
  lastMonth: 'monthBeforeLast',
  lastYear: 'yearBeforeLast',
};
const NAMED_LOOKUP_KEYS = new Set(Object.keys(PREVIOUS_CALENDAR_PRESET));

/**
 * Resolves the persisted `periodPreset` (+ custom bounds) into the actual
 * dateRange shape the rest of the app expects: { startDate, endDate, enabled }.
 * Always recomputed against `now` — this is what makes the tile "auto-refresh"
 * daily/weekly without any client-side timer.
 */
export function resolveCurrentRange(periodPreset, { customStart, customEnd } = {}, now = new Date()) {
  if (!periodPreset || periodPreset === ALL_TIME_PRESET.key) {
    return { startDate: null, endDate: null, enabled: false };
  }
  if (periodPreset === CUSTOM_PRESET.key) {
    return { startDate: customStart || null, endDate: customEnd || null, enabled: !!(customStart && customEnd) };
  }
  const { startDate, endDate } = getPresetRange(periodPreset, now);
  return { startDate, endDate, enabled: true };
}

/**
 * Resolves the Vorperiode for a given periodPreset + its already-resolved
 * current range, per the user story's table:
 *  - calendar presets  -> named previous calendar preset (full period)
 *  - rolling + custom  -> equal-duration window immediately preceding
 * Returns null when there is no meaningful previous period (periodPreset
 * "all", or current range not enabled/incomplete).
 */
export function resolvePreviousRange(periodPreset, currentRange, now = new Date()) {
  if (!periodPreset || periodPreset === ALL_TIME_PRESET.key) return null;
  if (!currentRange?.enabled || !currentRange.startDate || !currentRange.endDate) return null;

  if (NAMED_LOOKUP_KEYS.has(periodPreset)) {
    const prevKey = PREVIOUS_CALENDAR_PRESET[periodPreset];
    const { startDate, endDate } = getPresetRange(prevKey, now);
    return { startDate, endDate, enabled: true };
  }

  // Rolling presets + custom: equal-length window immediately before the
  // current range starts ("gleich langer Zeitraum unmittelbar davor").
  const currentStartMs = new Date(currentRange.startDate).getTime();
  const currentEndMs = new Date(currentRange.endDate).getTime();
  if (Number.isNaN(currentStartMs) || Number.isNaN(currentEndMs)) return null;

  const durationMs = currentEndMs - currentStartMs;
  const prevEndMs = currentStartMs - 1000;
  const prevStartMs = prevEndMs - durationMs;
  return {
    startDate: new Date(prevStartMs).toISOString(),
    endDate: new Date(prevEndMs).toISOString(),
    enabled: true,
  };
}

const toDateOnlyLabel = (iso) => (iso ? iso.slice(0, 10) : '');

/** Human-readable label for a resolved range, e.g. "2026-07-06 – 2026-07-12". */
export function formatRangeLabel(range) {
  if (!range?.enabled || !range.startDate) return null;
  const start = toDateOnlyLabel(range.startDate);
  const end = toDateOnlyLabel(range.endDate);
  return `${start} – ${end}`;
}

// Short German phrase for the 6 named presets ("ggü. letztem Monat", mirrors
// shadcn's "from last month"); German case ("letztem" vs "letzter") can't be
// derived from the nominative preset labels, so it's hard-coded.
// NOT YET TRANSLATED — belongs to the `kpi` domain pass, together with
// ALL_TIME_PRESET/CUSTOM_PRESET above and formatComparisonCaption below.
// See docs/i18n-migration.md.
const PREVIOUS_PERIOD_PHRASE = {
  today: 'ggü. gestern',
  yesterday: 'ggü. vorgestern',
  thisWeek: 'ggü. letzter Woche',
  lastWeek: 'ggü. vorletzter Woche',
  thisMonth: 'ggü. letztem Monat',
  lastMonth: 'ggü. vorletztem Monat',
  thisYear: 'ggü. letztem Jahr',
  lastYear: 'ggü. vorletztem Jahr',
};

/**
 * Short caption for what the trend indicator compares against. Named presets
 * get a natural phrase; everything else (yesterday/lastWeek/last2Weeks
 * selected directly, rolling presets, custom) falls back to the resolved
 * previous range's date label, which also satisfies "bei Custom wird die
 * verwendete Vorperiode kenntlich gemacht".
 */
export function formatComparisonCaption(periodPreset, previousRangeLabel) {
  return PREVIOUS_PERIOD_PHRASE[periodPreset] || (previousRangeLabel ? `ggü. ${previousRangeLabel}` : previousRangeLabel);
}

/**
 * One-time migration fallback for configs saved before `periodPreset`
 * existed: derive a best-guess preset from the legacy resolved dateRange so
 * old tiles keep behaving the same instead of silently resetting.
 */
export function detectPeriodPresetFromDateRange(dateRange, now = new Date()) {
  if (!dateRange?.enabled) return ALL_TIME_PRESET.key;
  const startMs = dateRange.startDate ? new Date(dateRange.startDate).getTime() : null;
  const endMs = dateRange.endDate ? new Date(dateRange.endDate).getTime() : null;
  if (startMs == null || endMs == null) return CUSTOM_PRESET.key;

  const matches = (key) => {
    try {
      const r = getPresetRange(key, now);
      const rStart = new Date(r.startDate).getTime();
      const rEnd = new Date(r.endDate).getTime();
      return Math.abs(rStart - startMs) < 60000 && Math.abs(rEnd - endMs) < 60000;
    } catch {
      return false;
    }
  };

  const found = ALL_DATE_PRESET_KEYS.find(matches);
  return found || CUSTOM_PRESET.key;
}
